import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { postService, clientService, categoryService } from '@services/api'
import toast from 'react-hot-toast'
import AdminLayout from '../../layouts/AdminLayout'
import { useAuthStore } from '@store'

const storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const mediaUrl = (p) => { if (!p) return null; let s = String(p); const i = s.indexOf('/storage/'); if (i !== -1) s = s.slice(i + 9); return storageURL + '/' + s.replace(/^\/+/, '') }

const mapVideo = (p) => {
  const isVideoFile = (path) => /\.(mp4|mov|avi|webm|mkv)$/i.test(path)
  const videoFile = p.fichiers?.find(f => isVideoFile(f.chemin))
  const thumbFile = p.fichiers?.find(f => !isVideoFile(f.chemin))
  const prixNum = p.prix ? parseFloat(p.prix) : 0
  const newPriceNum = p.new_price ? parseFloat(p.new_price) : 0
  const discountPct = (newPriceNum > prixNum && newPriceNum > 0)
    ? Math.round((1 - prixNum / newPriceNum) * 100) : 0
  const conditionMap = { neuf: 'Neuf', occasion: 'Occasion', usage: 'Occasion', reconditionne: 'Reconditionné', reconditionné: 'Reconditionné' }
  const conditionRaw = p.etat_appareil || p.condition || ''
  const computedDuration = (() => {
    if (p.DateFinPost && p.created_at) {
      const days = Math.round((new Date(p.DateFinPost) - new Date(p.created_at)) / (1000 * 60 * 60 * 24))
      if (days > 0) return String(days)
    }
    const raw = parseInt(p.IndicePeriode || p.indiceperiode || 0)
    return String(raw > 0 ? raw : 0)
  })()
  return {
    id: p.post_id,
    title: p.libele,
    description: p.description,
    price: prixNum,
    oldPrice: newPriceNum > prixNum ? newPriceNum : null,
    discount: discountPct > 0 ? `${discountPct}%` : '',
    location: p.situationGeographique || p.location || '',
    condition: conditionMap[conditionRaw.toLowerCase()] || conditionRaw || '',
    indiceperiode: p.IndicePeriode || p.indiceperiode || '',
    duration: computedDuration,
    videoUrl: videoFile ? mediaUrl(videoFile.chemin) : null,
    image: thumbFile ? mediaUrl(thumbFile.chemin) : null,
    views: p.vues || 0,
    status: p.status,
    created_at: p.created_at,
    starts_at: p.DateDebutPost || p.created_at || null,
    expires_at: p.DateFinPost || null,
    post_categorie_id: p.post_categorie_id,
    category_id: p.post_categorie_id,
    category: p.category?.libele_categorie || '',
    client_id: p.post_client_id,
    external_client_id: p.external_client_id || null,
    client_name: p.client ? [p.client.nom, p.client.prenom].filter(Boolean).join(' ') : '',
    client_phone: p.client?.numero || '',
    post_staff_id: p.post_staff_id,
  }
}

const AdminVideosPage = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [sortBy, setSortBy] = useState('date_desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(6) // 6 vidéos par page
  const [showModal, setShowModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [clients, setClients] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedVideos, setSelectedVideos] = useState([])
  const [selectAll, setSelectAll] = useState(false)

  // Récupérer les rôles de l'utilisateur
  const getCurrentUserRoles = () => {
    const storedUser = localStorage.getItem('admin_user') || localStorage.getItem('user')
    if (!storedUser) return []
    try {
      const userData = JSON.parse(storedUser)
      const normalize = (r) => (typeof r === 'string' ? r : r?.name ?? '')
      if (Array.isArray(userData.roles)) return userData.roles.map(normalize)
      if (typeof userData.roles === 'string') return userData.roles.split(',').map(r => r.trim())
      if (userData.role) return [normalize(userData.role)]
    } catch (e) {}
    return []
  }

  const userRoles = getCurrentUserRoles()
  const n = (r) => r.toLowerCase()
  const isSuperAdmin = userRoles.length === 0 || userRoles.some(r => n(r) === 'super admin')
  const isAdmin = userRoles.some(r => n(r) === 'admin')
  const isSuperviseur = userRoles.some(r => n(r) === 'superviseur')
  const isAnimateur = userRoles.some(r => n(r) === 'animateur')
  const isTechnicien = userRoles.some(r => n(r) === 'technicien')
  const isClient = userRoles.some(r => n(r) === 'client')

  const getUserId = () => {
    if (user?.users_id) return user.users_id
    if (user?.client_id) return user.client_id
    if (user?.id) return user.id
    if (user?.user_id) return user.user_id

    try {
      const storedUser = localStorage.getItem('kudhi_user') || localStorage.getItem('admin_user') || localStorage.getItem('user')
      if (storedUser) {
        const userData = JSON.parse(storedUser)
        return userData.users_id || userData.client_id || userData.id || userData.user_id
      }
    } catch (e) {
      console.error('Erreur parsing user from localStorage:', e)
    }
    return null
  }

  const currentClientId = getUserId()

  const isOwner = (video) => {
    if (isClient) {
      return video.client_id === currentClientId || video.client_id === String(currentClientId)
    }
    // Pour les staff (Animateur, etc.): vérifier si c'est eux qui ont créé la vidéo
    return video.post_staff_id === currentClientId || 
           video.post_staff_id === String(currentClientId) ||
           Number(video.post_staff_id) === Number(currentClientId)
  }

  const getPermissions = (video) => {
    if (isSuperAdmin || isAdmin) {
      return {
        canView: true,
        canEdit: true,
        canArchive: true,
        canDelete: true // Vraie suppression
      }
    }

    // Superviseur : peut voir, modifier et archiver toutes les vidéos
    if (isSuperviseur) {
      return {
        canView: true,
        canEdit: true,
        canArchive: true,
        canDelete: false
      }
    }

    // Autres rôles : 3 boutons sur leurs propres vidéos (Voir, Modifier, Supprimer)
    // Le bouton "Supprimer" archive au lieu de supprimer
    if (isOwner(video)) {
      return {
        canView: true,
        canEdit: true,
        canArchive: false, // Pas de bouton Archiver séparé
        canDelete: true    // Bouton Supprimer qui archive
      }
    }

    // Par défaut : lecture seule
    return {
      canView: true,
      canEdit: false,
      canArchive: false,
      canDelete: false
    }
  }
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    discount: '',
    condition: '',
    client_id: '',
    client_type: 'existing',
    external_client_name: '',
    external_client_phone: '',
    category: '',
    location: '',
    duration: '30',
    videoFile: null,
    thumbnail: null
  })
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    price: '',
    discount: '',
    condition: '',
    category: '',
    location: '',
    duration: '',
    client_id: '',
    client_type: 'existing',
    external_client_name: '',
    external_client_phone: '',
    videoFile: null,
    thumbnail: null
  })

  useEffect(() => {
    loadVideos()
    loadClients()
    loadCategories()

    // Écouter l'événement d'ouverture du modal depuis le layout
    const handleOpenModal = () => setShowModal(true)
    window.addEventListener('openAddVideoModal', handleOpenModal)

    return () => {
      window.removeEventListener('openAddVideoModal', handleOpenModal)
    }
  }, [])

  // Réinitialiser à la page 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterStatus, sortBy])

  const loadVideos = async () => {
    try {
      const params = {
        type: 1,
        staff_only: 1,
        ...( (!isSuperAdmin && !isAdmin) ? { post_staff: currentClientId } : {} )
      }

      const result = await postService.getPosts(params)
      setVideos((result?.data || []).map(mapVideo))
    } catch (error) {
      console.error('Error loading videos:', error)
      setVideos([])
    } finally {
      setLoading(false)
    }
  }

  const loadClients = async () => {
    try {
      const result = await clientService.getClients()
      setClients(result.data || [])
    } catch (error) {
      console.error('Error loading clients:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const result = await categoryService.getCategories()
      setCategories(result.data || [])
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.videoFile) {
      toast.error('⚠️ Veuillez sélectionner une vidéo')
      return
    }

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('title', formData.title)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('prix', formData.price)
      if (formData.discount) {
        formDataToSend.append('discount', formData.discount)
      }
      if (formData.condition) {
        formDataToSend.append('condition', formData.condition)
      }
      formDataToSend.append('post_categorie_id', formData.category)
      formDataToSend.append('location', formData.location)
      formDataToSend.append('type', '1') // 1 = vidéo
      formDataToSend.append('duration', formData.duration)
      formDataToSend.append('files[]', formData.videoFile)
      formDataToSend.append('from_admin_panel', 'true')

      if (formData.thumbnail) {
        formDataToSend.append('files[]', formData.thumbnail)
      }

      // Validation client
      if (formData.client_type === 'existing' && !formData.client_id) {
        toast.error('Veuillez sélectionner un client')
        return
      }
      if (formData.client_type === 'external' && (!formData.external_client_name || !formData.external_client_phone)) {
        toast.error('Veuillez remplir les informations du client externe')
        return
      }

      // Client associé à la vidéo
      if (formData.client_type === 'existing') {
        const clientId = formData.client_id || ''
        formDataToSend.append('client_id', clientId)
        formDataToSend.append('post_client_id', clientId)
      } else {
        formDataToSend.append('external_client', '1')
        formDataToSend.append('external_client_name', formData.external_client_name)
        formDataToSend.append('external_client_phone', formData.external_client_phone)
      }

      const staffCreatorId = user?.users_id || user?.client_id || user?.id
      if (staffCreatorId) {
        formDataToSend.append('staff_creator_id', staffCreatorId)
      }

      const result = await postService.createPost(formDataToSend)

      if (result) {
        toast.success('✨ Vidéo créée avec succès!')
        setShowModal(false)
        loadVideos()
        setFormData({
          title: '',
          description: '',
          price: '',
          discount: '',
          condition: '',
          client_id: '',
          client_type: 'existing',
          external_client_name: '',
          external_client_phone: '',
          category: '',
          location: '',
          duration: '30',
          videoFile: null,
          thumbnail: null
        })
      }
    } catch (error) {
      console.error('Error creating video:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la création de la vidéo')
    }
  }

  const handleVideoChange = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('video/')) {
      setFormData({ ...formData, videoFile: file })
    } else {
      toast.error('⚠️ Veuillez sélectionner un fichier vidéo valide')
    }
  }

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith('image/')) {
      setFormData({ ...formData, thumbnail: file })
    } else {
      toast.error('⚠️ Veuillez sélectionner une image valide')
    }
  }

  const handleView = (video) => {
    setSelectedVideo(video)
    setShowViewModal(true)
  }

  const handleEditClick = (video) => {
    setSelectedVideo(video)
    const discountValue = video.discount ? video.discount.toString().replace('%', '').trim() : ''
    const isExternal = !!video.external_client_id
    setEditFormData({
      title: video.title || '',
      description: video.description || '',
      price: video.price || '',
      discount: discountValue,
      condition: video.condition || '',
      category: video.category_id || video.post_categorie_id || '',
      location: video.location || '',
      duration: video.duration && video.duration !== '0' ? video.duration : '',
      client_id: isExternal ? '' : (video.client_id || ''),
      client_type: isExternal ? 'external' : 'existing',
      external_client_name: isExternal ? video.client_name : '',
      external_client_phone: isExternal ? video.client_phone : '',
      videoFile: null,
      thumbnail: null
    })
    setShowEditModal(true)
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    if (!selectedVideo) return

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('post_id', selectedVideo.id)
      formDataToSend.append('title', editFormData.title)
      formDataToSend.append('description', editFormData.description)
      formDataToSend.append('price', editFormData.price)
      if (editFormData.discount) {
        formDataToSend.append('discount', editFormData.discount)
      }
      formDataToSend.append('category', editFormData.category)
      formDataToSend.append('location', editFormData.location)
      formDataToSend.append('delivery', 'gratuite')
      if (editFormData.condition) {
        formDataToSend.append('condition', editFormData.condition)
      }
      if (editFormData.duration) {
        formDataToSend.append('duration', editFormData.duration)
      }

      // Client
      if (editFormData.client_type === 'existing') {
        formDataToSend.append('client_id', editFormData.client_id || '')
        formDataToSend.append('post_client_id', editFormData.client_id || '')
      } else {
        formDataToSend.append('external_client', '1')
        formDataToSend.append('external_client_name', editFormData.external_client_name || '')
        formDataToSend.append('external_client_phone', editFormData.external_client_phone || '')
      }

      // Ajouter la nouvelle vidéo si elle est fournie
      if (editFormData.videoFile) {
        formDataToSend.append('video', editFormData.videoFile)
      }

      // Ajouter la nouvelle miniature si elle est fournie
      if (editFormData.thumbnail) {
        formDataToSend.append('thumbnail', editFormData.thumbnail)
      }

      await postService.updatePost(selectedVideo.id, formDataToSend)
      toast.success('✅ Vidéo modifiée avec succès')
      setShowEditModal(false)
      setSelectedVideo(null)
      setEditFormData({
        title: '',
        description: '',
        price: '',
        discount: '',
        condition: '',
        category: '',
        location: '',
        duration: '',
        client_id: '',
        client_type: 'existing',
        external_client_name: '',
        external_client_phone: '',
        videoFile: null,
        thumbnail: null
      })
      loadVideos()
    } catch (error) {
      console.error('Error updating video:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la modification')
    }
  }

  const handleDelete = async () => {
    if (!selectedVideo) return

    try {
      // Si l'utilisateur est Super Admin ou Admin, vraie suppression
      // Sinon, archivage (status = 2) - mais l'utilisateur croit que c'est une suppression
      if (isSuperAdmin || isAdmin) {
        await postService.deletePost(selectedVideo.id)
        toast.success('🗑️ Vidéo supprimée avec succès')
        setShowDeleteModal(false)
        setSelectedVideo(null)
        loadVideos()
      } else {
        // Pour les autres utilisateurs : archivage au lieu de suppression (invisible pour eux)
        await postService.archivePost(selectedVideo.id)
        toast.success('🗑️ Vidéo supprimée avec succès')
        setShowDeleteModal(false)
        setSelectedVideo(null)

        // Mettre à jour immédiatement le state local
        setVideos(prevVideos =>
          prevVideos.map(v =>
            v.id === selectedVideo.id
              ? { ...v, status: 2 }
              : v
          )
        )

        // Recharger après un court délai
        setTimeout(() => {
          loadVideos()
        }, 300)
      }
    } catch (error) {
      console.error('Error deleting/archiving video:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression')
    }
  }

  const handleDeleteClick = (video) => {
    setSelectedVideo(video)
    setShowDeleteModal(true)
  }

  const toggleVideoStatus = async () => {
    if (!selectedVideo) return

    try {
      await postService.archivePost(selectedVideo.id)
      toast.success(`✅ Vidéo ${selectedVideo.status === 3 ? 'désarchivée' : 'archivée'} avec succès`)
      setShowArchiveModal(false)
      setSelectedVideo(null)
      loadVideos()
    } catch (error) {
      console.error('Error toggling video status:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la modification du statut')
    }
  }

  const handleArchiveClick = (video) => {
    setSelectedVideo(video)
    setShowArchiveModal(true)
  }

  const filteredVideos = videos.filter(video => {
    const titre = video.titre || video.libele || ''
    const clientNom = video.client_name || ''
    const matchesSearch = titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         clientNom.toLowerCase().includes(searchTerm.toLowerCase())
    
    if (filterStatus === 'all') return matchesSearch
    if (filterStatus === 'active') {
      return matchesSearch && video.status === 2
    }
    if (filterStatus === 'archived') {
      return matchesSearch && video.status === 3
    }
    if (filterStatus === 'pending') {
      return matchesSearch && video.status === 1
    }
    return matchesSearch
  })
  
  // Appliquer le tri
  const sortedVideos = [...filteredVideos].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0)
      case 'date_asc':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0)
      case 'name_asc':
        return ((a.titre || a.libele) || '').localeCompare((b.titre || b.libele) || '')
      case 'name_desc':
        return ((b.titre || b.libele) || '').localeCompare((a.titre || a.libele) || '')
      case 'views_desc':
        return (b.views || 0) - (a.views || 0)
      case 'views_asc':
        return (a.views || 0) - (b.views || 0)
      default:
        return 0
    }
  })
  
  console.log('Filtered videos:', sortedVideos)
  console.log('Sample video data:', sortedVideos[0])

  // Pagination
  const totalPages = Math.ceil(sortedVideos.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentVideos = sortedVideos.slice(startIndex, endIndex)

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400 text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Vidéos</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Gérer toutes les vidéos de la plateforme
              </p>
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Nouvelle vidéo
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Total</p>
                <p className="text-2xl font-bold text-gray-800">{videos.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Actives */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Actives</p>
                <p className="text-2xl font-bold text-green-600">
                  {videos.filter(v => v.status === 2).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Archivées */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Archivées</p>
                <p className="text-2xl font-bold text-orange-600">
                  {videos.filter(v => v.status === 3).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
          </div>

          {/* Vues totales */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Vues totales</p>
                <p className="text-2xl font-bold text-blue-600">
                  {videos.reduce((sum, v) => sum + (parseInt(v.views) || 0), 0)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-md p-6 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative md:col-span-2 lg:col-span-1">
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher par titre ou auteur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">✓ Actives</option>
              <option value="archived">📦 Archivées</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="date_desc">📅 Plus récentes</option>
              <option value="date_asc">📅 Plus anciennes</option>
              <option value="name_asc">🔤 Titre A-Z</option>
              <option value="name_desc">🔤 Titre Z-A</option>
              <option value="views_desc">👁️ Plus de vues</option>
              <option value="views_asc">👁️ Moins de vues</option>
            </select>
          </div>
        </div>

        {/* Table des vidéos */}
        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Liste des vidéos</h2>
              <span className="text-sm text-gray-500">{sortedVideos.length} vidéo{sortedVideos.length > 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        setSelectAll(e.target.checked)
                        if (e.target.checked) {
                          setSelectedVideos(currentVideos.map(v => v.video_id || v.post_id))
                        } else {
                          setSelectedVideos([])
                        }
                      }}
                      className="w-4 h-4 text-[#002A4E] border-gray-300 rounded focus:ring-2 focus:ring-amber-200 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'ajout</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Nb jours</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Jours restants</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date fin</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Vues</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentVideos.map((video, index) => {
                  const isActive = video.status === 2
                  const isArchived = video.status === 3
                  const isPending = video.status === 1
                  return (
                    <tr key={video.video_id || video.post_id} className={`hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${
                      isArchived ? 'bg-gray-100 opacity-60' : ''
                    }`}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedVideos.includes(video.video_id || video.post_id)}
                          onChange={(e) => {
                            const videoId = video.video_id || video.post_id
                            if (e.target.checked) {
                              setSelectedVideos([...selectedVideos, videoId])
                            } else {
                              setSelectedVideos(selectedVideos.filter(id => id !== videoId))
                              setSelectAll(false)
                            }
                          }}
                          className="w-4 h-4 text-[#002A4E] border-gray-300 rounded focus:ring-2 focus:ring-amber-200 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative w-20 h-12 bg-gray-900 rounded-md overflow-hidden group">
                          {video.images && video.images.length > 0 ? (
                            <>
                              <img 
                                src={video.images[1] || video.images[0]}
                                alt={video.titre || video.libele}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                </svg>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                          )}
                          <div className="absolute bottom-0.5 right-0.5 bg-black/80 text-white px-1 rounded text-[10px]">
                            <svg className="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-800">
                          {(video.titre || video.libele) && (video.titre || video.libele).split(' ').slice(0, 3).join(' ')}
                          {(video.titre || video.libele) && (video.titre || video.libele).split(' ').length > 3 && '...'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {video.description && video.description.split(' ').slice(0, 3).join(' ')}
                          {video.description && video.description.split(' ').length > 3 && '...'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-700 font-medium">
                          {video.client_name || (video.client_id ? `Client #${video.client_id}` : 'N/A')}
                        </p>
                        {video.client_phone && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {video.client_phone}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isActive ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Active
                          </span>
                        ) : isArchived ? (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                              <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                            Archivée
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {video.created_at ? new Date(video.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          {(() => {
                            const d = parseInt(video.duration)
                            return d > 0 ? `${d} ${d > 1 ? 'jours' : 'jour'}` : 'N/A'
                          })()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {video.expires_at ? (() => {
                          const now = new Date();
                          const expiryDate = new Date(video.expires_at);
                          const diffTime = expiryDate - now;
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                          if (diffDays < 0) {
                            return <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              Expiré
                            </span>;
                          } else if (diffDays === 0) {
                            return <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                              Aujourd'hui
                            </span>;
                          } else if (diffDays <= 3) {
                            return <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                              {diffDays} {diffDays === 1 ? 'jour' : 'jours'}
                            </span>;
                          } else if (diffDays <= 7) {
                            return <span className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                              {diffDays} jours
                            </span>;
                          } else {
                            return <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              {diffDays} jours
                            </span>;
                          }
                        })() : <span className="text-sm text-gray-400">N/A</span>}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {video.expires_at ? new Date(video.expires_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        <span className="flex items-center gap-1.5">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {video.views || video.vues || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {getPermissions(video).canView && (
                            <button 
                              onClick={() => handleView(video)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="Voir"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          )}
                          {getPermissions(video).canEdit && (
                            <button 
                              onClick={() => handleEditClick(video)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                              title="Modifier"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {getPermissions(video).canArchive && (
                            <button 
                              onClick={() => handleArchiveClick(video)}
                              className={`p-2 ${isArchived ? 'text-green-600 hover:bg-green-50' : 'text-orange-600 hover:bg-orange-50'} rounded-md transition-colors`}
                              title={isArchived ? 'Désarchiver' : 'Archiver'}
                            >
                              {isArchived ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                              )}
                            </button>
                          )}
                          {getPermissions(video).canDelete && (
                            <button 
                              onClick={() => handleDeleteClick(video)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              title="Supprimer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {sortedVideos.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">🎥</div>
                <p className="text-lg">Aucune vidéo trouvée</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {sortedVideos.length > 0 && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, sortedVideos.length)} sur {sortedVideos.length} vidéos
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-md transition-colors ${
                          currentPage === page
                            ? 'bg-[#FFD700] text-[#002A4E] font-semibold'
                            : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de création */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-md max-w-3xl w-full max-h-[90vh] flex flex-col animate-scaleIn border border-slate-200">
            {/* Header - Fixed */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0 rounded-t-md">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#002A4E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Créer une vidéo sponsorisée</h2>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body - Scrollable */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                
                {/* Info : Vidéo Staff */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm">
                      <p className="font-semibold text-blue-900 mb-1">📢 Vidéo Staff</p>
                      <p className="text-blue-700">
                        Cette vidéo sera publiée en tant que <strong>{user?.nom} {user?.prenom}</strong> (Staff)
                        et s'affichera dans la section publicité du dashboard public.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Type de client */}
                <div className="border-2 border-gray-200 rounded-md p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type de client <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="client_type"
                        value="existing"
                        checked={formData.client_type === 'existing'}
                        onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                        className="w-4 h-4 text-[#B8860B] focus:ring-amber-200"
                      />
                      <span className="ml-2 text-gray-700">Client existant</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="client_type"
                        value="external"
                        checked={formData.client_type === 'external'}
                        onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                        className="w-4 h-4 text-[#B8860B] focus:ring-amber-200"
                      />
                      <span className="ml-2 text-gray-700">Client externe (sans compte)</span>
                    </label>
                  </div>
                </div>

                {/* Sélection du client existant */}
                {formData.client_type === 'existing' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sélectionner le client <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      required
                    >
                      <option value="">-- Choisir un client --</option>
                      {clients.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.nom} {client.prenom} - {client.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Informations client externe */}
                {formData.client_type === 'external' && (
                  <div className="space-y-4 border-2 border-orange-200 rounded-md p-4 bg-orange-50">
                    <div className="flex items-center gap-2 text-orange-700 mb-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-semibold">Informations du client externe</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.external_client_name}
                        onChange={(e) => setFormData({ ...formData, external_client_name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        placeholder="Ex: Jean Kouassi"
                        required={formData.client_type === 'external'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Numéro de téléphone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.external_client_phone}
                        onChange={(e) => setFormData({ ...formData, external_client_phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        placeholder="Ex: +225 07 XX XX XX XX"
                        required={formData.client_type === 'external'}
                      />
                    </div>
                  </div>
                )}

                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                    placeholder="Ex: iPhone 16 Pro - Découvrez toutes les nouveautés"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                    placeholder="Description détaillée de la vidéo..."
                    required
                  />
                </div>

                {/* Prix et Remise */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix (FCFA) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      placeholder="Ex: 500000"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remise (%)
                    </label>
                    <select
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white"
                    >
                      <option value="">Aucune remise</option>
                      <option value="5%">5%</option>
                      <option value="7%">7%</option>
                      <option value="10%">10%</option>
                      <option value="15%">15%</option>
                      <option value="20%">20%</option>
                      <option value="25%">25%</option>
                      <option value="30%">30%</option>
                      <option value="35%">35%</option>
                      <option value="40%">40%</option>
                      <option value="50%">50%</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Le prix réduit sera calculé automatiquement
                    </p>
                  </div>
                </div>

                {/* Catégorie et Localisation */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Catégorie <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map((cat) => (
                        <option key={cat.categorie_id} value={cat.categorie_id}>
                          {cat.libele_categorie}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Localisation <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      placeholder="Ex: Abidjan, Cocody"
                      required
                    />
                  </div>
                </div>

                {/* État du produit et Durée d'affichage */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      État du produit
                    </label>
                    <select
                      value={formData.condition}
                      onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Neuf">Neuf</option>
                      <option value="Occasion">Occasion</option>
                      <option value="Reconditionné">Reconditionné</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée d'affichage (jours) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                      placeholder="Ex: 30"
                      min="1"
                      max="365"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Nombre de jours pendant lesquels la vidéo sera visible (1-365 jours)
                    </p>
                  </div>
                </div>

                {/* Vidéo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier vidéo <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-6 hover:border-[#F5B700] transition-colors">
                    <input
                      type="file"
                      onChange={handleVideoChange}
                      accept="video/*"
                      className="hidden"
                      id="video-upload"
                    />
                    <label htmlFor="video-upload" className="cursor-pointer flex flex-col items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-md flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {formData.videoFile ? formData.videoFile.name : 'Cliquez pour sélectionner une vidéo'}
                      </p>
                      <p className="text-xs text-gray-500">MP4, MOV, AVI (max. 500MB)</p>
                    </label>
                  </div>
                </div>

                {/* Miniature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Miniature (optionnel)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-6 hover:border-[#F5B700] transition-colors">
                    <input
                      type="file"
                      onChange={handleThumbnailChange}
                      accept="image/*"
                      className="hidden"
                      id="thumbnail-upload"
                    />
                    <label htmlFor="thumbnail-upload" className="cursor-pointer flex flex-col items-center">
                      {formData.thumbnail ? (
                        <div className="w-full">
                          <img
                            src={URL.createObjectURL(formData.thumbnail)}
                            alt="Aperçu"
                            className="w-full h-32 object-cover rounded-md mb-3"
                          />
                          <p className="text-sm text-gray-600 text-center">{formData.thumbnail.name}</p>
                        </div>
                      ) : (
                        <>
                          <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center mb-3">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">Cliquez pour ajouter une miniature</p>
                          <p className="text-xs text-gray-500">JPG, PNG (recommandé: 1280x720)</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer - Fixed */}
              <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-md flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium"
                >
                  Créer la vidéo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Voir les détails */}
      {showViewModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn backdrop-blur-sm">
          <div className="bg-white rounded-md max-w-5xl w-full max-h-[90vh] flex flex-col animate-scaleIn border border-slate-200">
            {/* Header */}
            <div className="px-8 py-6 bg-blue-50/50 border-b-2 border-blue-100 rounded-t-md flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-blue-600">Détails de la vidéo</h2>
                  <p className="text-blue-500 text-sm mt-0.5">Informations complètes</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedVideo(null)
                }}
                className="p-2 hover:bg-blue-50 rounded-md transition-all duration-200"
              >
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-8 space-y-8 overflow-y-auto flex-1 bg-gray-50">
              {/* Vidéo */}
              <div className="bg-white rounded-md p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Vidéo</h3>
                </div>
                {selectedVideo.images && selectedVideo.images[0] && (
                  <video 
                    src={selectedVideo.images[0]} 
                    controls 
                    className="w-full rounded-md border-2 border-gray-200 "
                  />
                )}
              </div>

              {/* Informations principales */}
              <div className="bg-white rounded-md p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-6">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Informations générales</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-md p-4 border border-blue-200">
                    <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Titre</label>
                    <p className="text-base font-bold text-gray-900 mt-2">{selectedVideo.name || selectedVideo.libele}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-md p-4 border border-purple-200">
                    <label className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Catégorie</label>
                    <p className="mt-2">
                      <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-[#002A4E] rounded-md text-sm font-semibold ">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {selectedVideo.category}
                      </span>
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-md p-4 border border-green-200">
                    <label className="text-xs font-semibold text-green-600 uppercase tracking-wide">Prix</label>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-green-700">
                        {selectedVideo.price?.toLocaleString('fr-FR')} <span className="text-sm">FCFA</span>
                      </p>
                      {selectedVideo.oldPrice && (
                        <p className="text-sm text-gray-500 line-through mt-1">
                          {selectedVideo.oldPrice?.toLocaleString('fr-FR')} FCFA
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-md p-4 border border-orange-200">
                    <label className="text-xs font-semibold text-orange-600 uppercase tracking-wide">Client</label>
                    <p className="text-base font-semibold text-gray-900 mt-2">{selectedVideo.client_name}</p>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {selectedVideo.client_phone}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-md p-4 border border-red-200">
                    <label className="text-xs font-semibold text-red-600 uppercase tracking-wide">Localisation</label>
                    <p className="text-base font-semibold text-gray-900 mt-2 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {selectedVideo.location}
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-md p-4 border border-indigo-200">
                    <label className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Livraison & État</label>
                    <div className="mt-2 flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-md text-sm font-semibold ${
                        selectedVideo.delivery === 'gratuite' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-orange-600 text-white'
                      }`}>
                        {selectedVideo.delivery === 'gratuite' ? '🎁 Gratuite' : '💰 Payante'}
                      </span>
                      <span className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm font-semibold">
                        {selectedVideo.condition || 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 rounded-md p-4 border border-cyan-200">
                    <label className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">Statistiques</label>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span className="text-base font-bold text-gray-900">{selectedVideo.views || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-700">
                          {selectedVideo.created_at ? new Date(selectedVideo.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : selectedVideo.date}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-cyan-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Début:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedVideo.created_at ? new Date(selectedVideo.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-gray-600">Fin:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedVideo.expires_at || selectedVideo.DateFinPost ? new Date(selectedVideo.expires_at || selectedVideo.DateFinPost).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-md p-4 border border-teal-200">
                    <label className="text-xs font-semibold text-teal-600 uppercase tracking-wide">Temps restant</label>
                    <div className="mt-2">
                      {(selectedVideo.expires_at || selectedVideo.DateFinPost) ? (() => {
                        const now = new Date();
                        const expiryDate = new Date(selectedVideo.expires_at || selectedVideo.DateFinPost);
                        const diffTime = expiryDate - now;
                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                        
                        if (diffDays < 0) {
                          return <span className="px-4 py-2 bg-red-100 text-red-700 rounded-md text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Expiré
                          </span>;
                        } else if (diffDays === 0) {
                          return <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-md text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            Expire aujourd'hui
                          </span>;
                        } else if (diffDays === 1) {
                          return <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-md text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            1 jour restant
                          </span>;
                        } else if (diffDays <= 7) {
                          return <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-md text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {diffDays} jours restants
                          </span>;
                        } else {
                          return <span className="px-4 py-2 bg-green-100 text-green-700 rounded-md text-sm font-bold inline-flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {diffDays} jours restants
                          </span>;
                        }
                      })() : <span className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md text-sm font-medium">Non défini</span>}
                    </div>
                    <div className="mt-3 pt-3 border-t border-teal-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Durée d'affichage:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedVideo.indiceperiode ? `${selectedVideo.indiceperiode} ${selectedVideo.indiceperiode > 1 ? 'jours' : 'jour'}` : selectedVideo.created_at && (selectedVideo.expires_at || selectedVideo.DateFinPost) ? (() => {
                            const startDate = new Date(selectedVideo.created_at);
                            const endDate = new Date(selectedVideo.expires_at || selectedVideo.DateFinPost);
                            const diffTime = endDate - startDate;
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            return `${diffDays} ${diffDays > 1 ? 'jours' : 'jour'}`;
                          })() : selectedVideo.duration ? `${selectedVideo.duration} ${selectedVideo.duration > 1 ? 'jours' : 'jour'}` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-md p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-800">Description détaillée</h3>
                </div>
                <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                  <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedVideo.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 py-4 border-t border-slate-200 bg-white rounded-b-md flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedVideo(null)
                }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-md hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier */}
      {showEditModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-md max-w-3xl w-full max-h-[90vh] flex flex-col animate-scaleIn border border-slate-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Modifier la vidéo</h2>
              </div>
              <button 
                onClick={() => {
                  setShowEditModal(false)
                  setSelectedVideo(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1">

                {/* Type de client */}
                <div className="border-2 border-gray-200 rounded-md p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type de client <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="existing"
                        checked={editFormData.client_type === 'existing'}
                        onChange={(e) => setEditFormData({ ...editFormData, client_type: e.target.value })}
                        className="w-4 h-4 text-[#B8860B] focus:ring-amber-200"
                      />
                      <span className="ml-2 text-gray-700">Client existant</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="external"
                        checked={editFormData.client_type === 'external'}
                        onChange={(e) => setEditFormData({ ...editFormData, client_type: e.target.value })}
                        className="w-4 h-4 text-[#B8860B] focus:ring-amber-200"
                      />
                      <span className="ml-2 text-gray-700">Client externe (sans compte)</span>
                    </label>
                  </div>
                </div>

                {/* Sélection du client existant */}
                {editFormData.client_type === 'existing' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sélectionner le client <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editFormData.client_id}
                      onChange={(e) => setEditFormData({ ...editFormData, client_id: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">-- Choisir un client --</option>
                      {clients.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.nom} {client.prenom} - {client.email}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Informations client externe */}
                {editFormData.client_type === 'external' && (
                  <div className="space-y-4 border-2 border-orange-200 rounded-md p-4 bg-orange-50">
                    <div className="flex items-center gap-2 text-orange-700 mb-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-semibold">Informations du client externe</span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editFormData.external_client_name}
                        onChange={(e) => setEditFormData({ ...editFormData, external_client_name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ex: Jean Kouassi"
                        required={editFormData.client_type === 'external'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Numéro de téléphone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={editFormData.external_client_phone}
                        onChange={(e) => setEditFormData({ ...editFormData, external_client_phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Ex: +225 07 XX XX XX XX"
                        required={editFormData.client_type === 'external'}
                      />
                    </div>
                  </div>
                )}

                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ex: iPhone 16 Pro - Découvrez toutes les nouveautés"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Description détaillée de la vidéo..."
                    required
                  />
                </div>

                {/* Prix et Remise */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prix (FCFA) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={editFormData.price}
                      onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: 500000"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remise (%)
                    </label>
                    <select
                      value={editFormData.discount}
                      onChange={(e) => setEditFormData({ ...editFormData, discount: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">Aucune remise</option>
                      <option value="5%">5%</option>
                      <option value="7%">7%</option>
                      <option value="10%">10%</option>
                      <option value="15%">15%</option>
                      <option value="20%">20%</option>
                      <option value="25%">25%</option>
                      <option value="30%">30%</option>
                      <option value="35%">35%</option>
                      <option value="40%">40%</option>
                      <option value="50%">50%</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Le prix réduit sera calculé automatiquement
                    </p>
                  </div>
                </div>

                {/* Catégorie et Localisation */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Catégorie <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editFormData.category}
                      onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map((cat) => (
                        <option key={cat.categorie_id} value={cat.categorie_id}>
                          {cat.libele_categorie}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Localisation <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editFormData.location}
                      onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: Abidjan, Cocody"
                      required
                    />
                  </div>
                </div>

                {/* État du produit et Durée d'affichage */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      État du produit
                    </label>
                    <select
                      value={editFormData.condition}
                      onChange={(e) => setEditFormData({ ...editFormData, condition: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                    >
                      <option value="">Sélectionner</option>
                      <option value="Neuf">Neuf</option>
                      <option value="Occasion">Occasion</option>
                      <option value="Reconditionné">Reconditionné</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Durée d'affichage (jours) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={editFormData.duration}
                      onChange={(e) => setEditFormData({ ...editFormData, duration: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Ex: 30"
                      min="1"
                      max="365"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Nombre de jours pendant lesquels la vidéo sera visible (1-365 jours)
                    </p>
                  </div>
                </div>

                {/* Nouvelle vidéo (optionnel) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fichier vidéo (optionnel - remplace la vidéo actuelle)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-6 hover:border-green-500 transition-colors">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          setEditFormData({ ...editFormData, videoFile: file })
                        }
                      }}
                      accept="video/*"
                      className="hidden"
                      id="edit-video-upload"
                    />
                    <label htmlFor="edit-video-upload" className="cursor-pointer flex flex-col items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-md flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {editFormData.videoFile ? editFormData.videoFile.name : 'Cliquez pour sélectionner une vidéo'}
                      </p>
                      <p className="text-xs text-gray-500">MP4, MOV, AVI (max. 500MB)</p>
                    </label>
                  </div>
                </div>

                {/* Miniature (optionnel) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Miniature (optionnel)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-6 hover:border-green-500 transition-colors">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          setEditFormData({ ...editFormData, thumbnail: file })
                        }
                      }}
                      accept="image/*"
                      className="hidden"
                      id="edit-thumbnail-upload"
                    />
                    <label htmlFor="edit-thumbnail-upload" className="cursor-pointer flex flex-col items-center">
                      {editFormData.thumbnail ? (
                        <div className="w-full">
                          <img
                            src={URL.createObjectURL(editFormData.thumbnail)}
                            alt="Aperçu"
                            className="w-full h-32 object-cover rounded-md mb-3"
                          />
                          <p className="text-sm text-gray-600 text-center">{editFormData.thumbnail.name}</p>
                        </div>
                      ) : (
                        <>
                          <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center mb-3">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">Cliquez pour ajouter une miniature</p>
                          <p className="text-xs text-gray-500">JPG, PNG (recommandé: 1280x720)</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer - Fixed */}
              <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-md flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedVideo(null)
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-md transition-all font-medium"
                >
                  Enregistrer les modifications
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {showDeleteModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-md max-w-md w-full animate-scaleIn border border-slate-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Supprimer la vidéo</h3>
                  <p className="text-sm text-gray-500 mt-1">Cette action est irréversible</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Êtes-vous sûr de vouloir supprimer cette vidéo ?
              </p>
              <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                <p className="font-semibold text-gray-800">{selectedVideo.name || selectedVideo.libele}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedVideo.category}</p>
              </div>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">
                  Attention : Cette vidéo sera définitivement supprimée de la base de données.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-md flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedVideo(null)
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-md transition-all font-medium"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmation Archivage */}
      {showArchiveModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-md max-w-md w-full animate-scaleIn border border-slate-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 ${selectedVideo.status === 3 ? 'bg-green-100' : 'bg-orange-100'} rounded-full flex items-center justify-center`}>
                  {selectedVideo.status === 3 ? (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    {selectedVideo.status === 3 ? 'Désarchiver' : 'Archiver'} la vidéo
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedVideo.status === 3 ? 'Rendre visible à nouveau' : 'Masquer temporairement'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {selectedVideo.status === 3 
                  ? 'Cette vidéo sera à nouveau visible sur la plateforme.'
                  : 'Cette vidéo sera masquée mais pourra être restaurée plus tard.'}
              </p>
              <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                <p className="font-semibold text-gray-800">{selectedVideo.name || selectedVideo.libele}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedVideo.category}</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-md flex gap-3">
              <button
                onClick={() => {
                  setShowArchiveModal(false)
                  setSelectedVideo(null)
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={toggleVideoStatus}
                className={`flex-1 px-6 py-3 bg-gradient-to-r ${selectedVideo.status === 3 ? 'from-green-500 to-green-600' : 'from-orange-500 to-orange-600'} text-white rounded-md transition-all font-medium`}
              >
                Oui, {selectedVideo.status === 3 ? 'désarchiver' : 'archiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminVideosPage
